import * as RadixSelect from "@radix-ui/react-select";

import { PropsWithChildren, ReactNode, forwardRef } from "react";

import { Icon } from "react-migration/components/Icon";
import clsx from "clsx";

export enum SelectSize {
  SM = "sm",
  XS = "xs",
}

export type SelectProps = PropsWithChildren<{
  size?: `${SelectSize}`;
  placeholder?: string;
  triggerProps?: React.HTMLAttributes<HTMLButtonElement> & { [key: string]: any };
  viewportProps?: RadixSelect.SelectViewportProps;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  alignOffset?: number;
}> &
  RadixSelect.SelectProps;

function Select({
  children,
  disabled,
  size = SelectSize.SM,
  placeholder = "",
  triggerProps,
  viewportProps,
  side = "bottom",
  sideOffset = 0,
  align = "start",
  alignOffset,
  ...props
}: SelectProps) {
  return (
    <RadixSelect.Root disabled={disabled} {...props}>
      <RadixSelect.Trigger
        {...triggerProps}
        className={clsx(
          "w-full inline-flex items-center justify-between gap-2 rounded-lg border border-blue-grey-200 [&>span:first-child]:truncate",
          {
            "bg-white text-blue-grey-950": !disabled,
            "bg-blue-grey-100 text-blue-grey-400 pointer-events-none": disabled,
            "text-sm h-9 px-3": size === SelectSize.SM,
            "text-xs h-7 px-2": size === SelectSize.XS,
          }
        )}
        asChild
      >
        <div>
          <RadixSelect.Value className="truncate grow" placeholder={placeholder} />
          <RadixSelect.Icon className="shrink-0">
            <Icon
              icon="icon-lt-arrow-down-s-line"
              size={size === SelectSize.SM ? "sm" : "xs"}
              extraClasses={clsx("pointer-events-none", {
                "text-neutral-500": disabled,
                "text-lg": size === SelectSize.SM,
                "text-md": size === SelectSize.XS,
              })}
            />
          </RadixSelect.Icon>
        </div>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          className="z-55 overflow-hidden bg-white border rounded-lg shadow-md max-w-96 border-blue-grey-200"
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
        >
          <RadixSelect.ScrollUpButton className="flex items-center justify-center h-6 bg-white cursor-default text-blue-grey-600">
            <Icon icon="icon-lt-arrow-up-s-line" />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport className="px-2 py-3" {...viewportProps}>
            {children}
          </RadixSelect.Viewport>
          <RadixSelect.ScrollDownButton className="flex items-center justify-center h-6 bg-white cursor-default text-blue-grey-600">
            <Icon icon="icon-lt-arrow-down-s-line" />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

const SelectSeparator = () => <RadixSelect.Separator className="h-px m-1 bg-blue-grey-200" />;

type SelectOptionProps = {
  badge?: ReactNode;
  children: string | ReactNode;
  title?: string;
  itemClassName?: string;
} & Omit<RadixSelect.SelectItemProps, "children">;

const SelectOption = forwardRef<HTMLDivElement, SelectOptionProps>(
  ({ children, className, badge, title, itemClassName, ...props }, forwardedRef) => (
    <RadixSelect.Item
      className={clsx(
        "relative grow flex gap-2 justify-between h-8 select-none items-center rounded-lg px-2 text-sm leading-none data-disabled:cursor-not-allowed data-disabled:text-blue-grey-300 data-highlighted:bg-background-button-secondary-hover data-highlighted:text-text-base-active-hover data-highlighted:outline-hidden",
        className
      )}
      ref={forwardedRef ?? null}
      {...props}
    >
      <RadixSelect.ItemText asChild>
        <div className={clsx("truncate h-8 pt-[9px]", itemClassName)} title={title ?? (children as string)}>
          {children}
        </div>
      </RadixSelect.ItemText>{" "}
      {badge}
    </RadixSelect.Item>
  )
);

SelectOption.displayName = "SelectOption";

Select.Option = SelectOption;
Select.Separator = SelectSeparator;
Select.Size = SelectSize;

export { Select };
